import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Moon, SunMedium } from "lucide-react";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import FuxIcon from "@/components/FuxIcon";

const NAV_ITEMS = [
  { label: "Leistungen", href: "/#leistungen", section: true },
  { label: "Prozess", href: "/#prozess", section: true },
  { label: "Werkzeuge", href: "/#tool-proof", section: true },
  { label: "Anzeigen-Formate", href: "/#anzeigen-simulator", section: true },
  { label: "Cases", href: "/#projekte", section: true },
  { label: "FAQ", href: "/#faq", section: true },
  { label: "White-Label", href: "/white-label" },
];

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const location = useLocation();
  const { theme, setTheme } = useTheme();

  React.useEffect(() => { setOpen(false); }, [location.pathname]);
  React.useEffect(() => { setMounted(true); }, []);

  const isDark = mounted && theme === "dark";

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-slate-200 bg-white/85 backdrop-blur-xl dark:border-cyan-950/70 dark:bg-[#060b12]/95"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 hidden h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent dark:block" />

      {/* Logo + Nav row */}
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:h-16 sm:px-6 lg:px-8">
        <Link to="/" data-testid="logo-home-link" className="flex shrink-0 items-center gap-1 sm:gap-1.5">
          <FuxIcon
            className="size-7"
            color={isDark ? "#22d3ee" : "#0f172a"}
          />
          <div className="flex items-center">
            <span className="font-display text-[19px] font-bold tracking-tight text-slate-900 dark:text-slate-50 sm:text-[21px]">
              ads<span className="text-cyan-500 dark:text-cyan-400">FUX</span>
            </span>
          </div>
        </Link>

        {/* Center nav — desktop only */}
        <nav
          data-testid="primary-nav"
          className="hidden lg:flex items-center gap-1 overflow-x-auto scrollbar-none"
        >
          {NAV_ITEMS.map((item) => (
            item.section ? (
              <a
                key={item.label}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                data-track="nav_click"
                data-nav={item.label}
                className="inline-flex items-center whitespace-nowrap px-3 py-2 text-[13px] text-slate-600 transition-colors hover:text-slate-900 dark:font-mono dark:text-[10px] dark:uppercase dark:tracking-[0.24em] dark:text-slate-500 dark:hover:text-cyan-200"
              >
                {item.label}
              </a>
            ) : (
              <NavLink
                key={item.label}
                to={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                data-track="nav_click"
                data-nav={item.label}
                className={({ isActive }) =>
                  `inline-flex items-center whitespace-nowrap px-3 py-2 text-[13px] transition-colors dark:font-mono dark:text-[10px] dark:uppercase dark:tracking-[0.24em] ${
                    isActive
                      ? "text-slate-900 dark:text-cyan-200"
                      : "text-slate-600 hover:text-slate-900 dark:text-slate-500 dark:hover:text-cyan-200"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <button
            data-testid="theme-toggle-btn"
            data-track="theme_toggle"
            onClick={() => setTheme(isDark ? "light" : "dark")}
            className="inline-flex size-9 items-center justify-center rounded-md border border-slate-200 text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900 dark:border-cyan-950/80 dark:text-slate-400 dark:hover:border-cyan-700/50 dark:hover:text-cyan-200"
            aria-label={isDark ? "Helles Theme aktivieren" : "Dunkles Theme aktivieren"}
            type="button"
          >
            {isDark ? <SunMedium className="size-4" /> : <Moon className="size-4" />}
          </button>
          <Link to="/audit">
            <Button
              data-testid="header-audit-btn"
              data-track="cta_click"
              data-cta="audit"
              data-location="header"
              className="inline-flex h-9 items-center gap-1 rounded-md bg-slate-900 px-3 text-[13px] text-white hover:bg-slate-800 sm:gap-1.5 sm:pl-4 sm:pr-3 sm:text-sm dark:bg-cyan-500 dark:text-slate-950 dark:hover:bg-cyan-400"
            >
              <span className="sm:hidden">Audit</span>
              <span className="hidden sm:inline">Audit anfragen</span>
              <ArrowUpRight className="hidden size-4 sm:block" />
            </Button>
          </Link>
          <button
            data-testid="mobile-nav-toggle"
            data-track="mobile_nav_toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex size-9 items-center justify-center rounded-md border border-slate-200 text-slate-700 hover:border-slate-300 dark:border-cyan-950/80 dark:text-slate-300 dark:hover:border-cyan-700/50 lg:hidden"
            aria-label="Menü öffnen"
            type="button"
          >
            {open ? <X className="size-4" /> : <Menu className="size-4" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-nav"
          className="border-t border-slate-200 bg-white dark:border-cyan-950/70 dark:bg-[#081019] lg:hidden"
        >
          <ul className="max-w-7xl mx-auto px-6 py-3 grid grid-cols-1 gap-1">
            <li className="sm:hidden">
              <button
                onClick={() => setTheme(isDark ? "light" : "dark")}
                className="block w-full rounded-md border border-slate-200 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:border-cyan-950/80 dark:text-slate-300 dark:hover:bg-[#0d1722]"
                type="button"
              >
                Theme wechseln: {mounted ? (isDark ? "Light" : "Dark") : "Theme"}
              </button>
            </li>
            {NAV_ITEMS.map((item) => (
              <li key={item.label}>
                {item.section ? (
                  <a
                    href={item.href}
                    className="block rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-[#0d1722]"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link to={item.href} className="block rounded-md px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-[#0d1722]">
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}
